import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ZoomIn, ZoomOut, Home, MousePointerClick } from 'lucide-react';
import { MindmapNode, CompactNode } from './MindmapNode';
import { HorizontalConnection } from './MindmapConnections';
import { MINDMAP_DATA, getNodeColors } from './mockData';
import type { MindmapNode as MindmapNodeType } from './mockData';

interface StrategyMindmapProps {
  onNodeDetailClick: (node: MindmapNodeType) => void;
}

/**
 * Main mindmap visualization container.
 * Orchestrates layout, interactions, and animations.
 */
export function StrategyMindmap({ onNodeDetailClick }: StrategyMindmapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Start with root collapsed - user must click to explore
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [selectedLeaf, setSelectedLeaf] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  
  const isRootExpanded = expandedNodes.has('root');

  // Toggle node expansion
  const toggleNode = useCallback((nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
        // If collapsing root, also clear selected branch and leaf
        if (nodeId === 'root') {
          setSelectedBranch(null);
          setSelectedLeaf(null);
        }
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  // Select a branch (BUILD, BUY, HYBRID)
  const selectBranch = useCallback((branchId: string) => {
    setSelectedBranch(branchId);
    setSelectedLeaf(null);
    setExpandedNodes((prev) => new Set([...prev, branchId]));
  }, []);

  // Select a leaf node
  const selectLeaf = useCallback((leafId: string) => {
    setSelectedLeaf(leafId);
    setExpandedNodes((prev) => new Set([...prev, leafId]));
  }, []);

  // Zoom controls
  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.2, 2));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.2, 0.5));
  const handleResetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Get branch nodes
  const branches = MINDMAP_DATA.children || [];
  const selectedBranchData = branches.find((b) => b.id === selectedBranch);
  const leaves = selectedBranchData?.children || [];

  return (
    <div className="relative w-full h-full min-h-[400px] sm:min-h-[600px] overflow-x-auto overflow-y-hidden" ref={containerRef}>
      {/* Background grid pattern */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `
            linear-gradient(to right, #1a6394 1px, transparent 1px),
            linear-gradient(to bottom, #1a6394 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      {/* Zoom controls */}
      <div className="absolute top-4 right-4 z-20 flex flex-col gap-2">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleZoomIn}
          className="p-2 bg-[#063F6B]/80 backdrop-blur-sm rounded-lg border border-[#1a6394] text-slate-300 hover:text-white hover:border-amber-500/50 transition-colors"
        >
          <ZoomIn className="w-5 h-5" />
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleZoomOut}
          className="p-2 bg-[#063F6B]/80 backdrop-blur-sm rounded-lg border border-[#1a6394] text-slate-300 hover:text-white hover:border-amber-500/50 transition-colors"
        >
          <ZoomOut className="w-5 h-5" />
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleResetView}
          className="p-2 bg-[#063F6B]/80 backdrop-blur-sm rounded-lg border border-[#1a6394] text-slate-300 hover:text-white hover:border-amber-500/50 transition-colors"
        >
          <Home className="w-5 h-5" />
        </motion.button>
      </div>

      {/* Main content with zoom/pan */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
        }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        {/* Horizontal tree layout */}
        <div className="flex items-center gap-8">
          
          {/* ROOT NODE - Center */}
          <div className="flex flex-col items-center">
            <MindmapNode
              node={MINDMAP_DATA}
              level={0}
              isExpanded={isRootExpanded}
              onToggle={() => toggleNode('root')}
              onDetailClick={() => onNodeDetailClick(MINDMAP_DATA)}
              delay={0}
            />
            
            {/* Click hint - shown when root is collapsed */}
            <AnimatePresence>
              {!isRootExpanded && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ delay: 0.5 }}
                  className="mt-4 flex items-center gap-2 text-[#EAA059]"
                >
                  <motion.div
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <MousePointerClick className="w-5 h-5" />
                  </motion.div>
                  <span className="text-sm font-medium">Click to explore the framework</span>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Explanation hint - shown when root IS expanded */}
            <AnimatePresence>
              {isRootExpanded && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ delay: 0.3 }}
                  className="mt-4 max-w-[400px] text-center"
                >
                  <p className="text-sm text-slate-300 leading-relaxed">
                    Your main options are to <span className="text-[#EAA059] font-medium">Build</span>, <span className="text-[#EAA059] font-medium">Buy</span>, or <span className="text-[#EAA059] font-medium">Hybrid</span>.
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-2 text-[#EAA059]">
                    <span className="text-xs font-medium">Click each option to learn more</span>
                    <span className="text-lg">→</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* SVG Connections Layer */}
          <svg className="absolute inset-0 pointer-events-none overflow-visible" style={{ zIndex: 0 }}>
            <defs>
              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
          </svg>

          {/* BRANCH COLUMN */}
          <AnimatePresence>
            {isRootExpanded && (
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col gap-4"
              >
                {branches.map((branch, index) => {
                  const colors = getNodeColors(branch.id);
                  const isSelected = selectedBranch === branch.id;
                  
                  return (
                    <motion.div
                      key={branch.id}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + index * 0.1 }}
                      className="relative"
                    >
                      {/* Connection line to root */}
                      <svg
                        className="absolute -left-8 top-1/2 w-8 h-1 overflow-visible"
                        style={{ transform: 'translateY(-50%)' }}
                      >
                        <motion.line
                          x1="0"
                          y1="0"
                          x2="32"
                          y2="0"
                          stroke={isSelected ? '#EAA059' : '#1a6394'}
                          strokeWidth={isSelected ? 2 : 1.5}
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ duration: 0.3, delay: 0.2 + index * 0.1 }}
                        />
                      </svg>

                      <motion.button
                        onClick={() => selectBranch(branch.id)}
                        className={`
                          px-4 py-3 sm:px-6 sm:py-4 rounded-2xl min-w-[150px] sm:min-w-[200px]
                          ${colors.solid}
                          border ${isSelected ? 'border-white/60' : 'border-transparent'}
                          text-white text-left
                          shadow-lg hover:shadow-xl
                          transition-all duration-200
                        `}
                        whileHover={{ scale: 1.02, x: 4 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="font-semibold text-base">{branch.title}</div>
                        <div className="text-sm text-white/50">{branch.subtitle}</div>
                      </motion.button>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>

          {/* LEAF COLUMN */}
          <AnimatePresence>
            {selectedBranch && selectedBranchData && (
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col gap-3"
              >
                {leaves.map((leaf, index) => {
                  const colors = getNodeColors(leaf.id);
                  const isSelected = selectedLeaf === leaf.id;
                  
                  return (
                    <motion.div
                      key={leaf.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.08 }}
                      className="relative"
                    >
                      {/* Connection line */}
                      <svg
                        className="absolute -left-6 top-1/2 w-6 h-1 overflow-visible"
                        style={{ transform: 'translateY(-50%)' }}
                      >
                        <motion.line
                          x1="0"
                          y1="0"
                          x2="24"
                          y2="0"
                          stroke={isSelected ? '#EAA059' : '#1a6394'}
                          strokeWidth={isSelected ? 2 : 1}
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ duration: 0.2, delay: index * 0.05 }}
                        />
                      </svg>

                      <CompactNode
                        node={leaf}
                        isSelected={isSelected}
                        onClick={() => selectLeaf(leaf.id)}
                        delay={index * 0.05}
                      />
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>

          {/* DETAIL PANEL */}
          <AnimatePresence>
            {selectedLeaf && (
              <motion.div
                initial={{ opacity: 0, x: -30, width: 0 }}
                animate={{ opacity: 1, x: 0, width: 'auto' }}
                exit={{ opacity: 0, x: -30, width: 0 }}
                transition={{ duration: 0.4 }}
                className="max-w-md"
              >
                {leaves
                  .filter((l) => l.id === selectedLeaf)
                  .map((leaf) => (
                    <MindmapNode
                      key={leaf.id}
                      node={leaf}
                      level={2}
                      isExpanded={true}
                      onToggle={() => {}}
                      onDetailClick={() => onNodeDetailClick(leaf)}
                      delay={0.1}
                    />
                  ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 flex items-center gap-6 text-xs text-slate-400">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-500" />
          <span>Advantage</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-rose-500" />
          <span>Challenge</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-slate-400" />
          <span>Consideration</span>
        </div>
      </div>

      {/* Breadcrumb trail */}
      <div className="absolute top-4 left-4 flex items-center gap-2 text-sm">
        <motion.button
          onClick={() => {
            setSelectedBranch(null);
            setSelectedLeaf(null);
          }}
          className="text-amber-400 hover:text-amber-300 transition-colors"
        >
          Strategy
        </motion.button>
        
        {selectedBranch && (
          <>
            <span className="text-slate-500">/</span>
            <motion.button
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => setSelectedLeaf(null)}
              className="text-amber-400 hover:text-amber-300 transition-colors"
            >
              {selectedBranchData?.title}
            </motion.button>
          </>
        )}
        
        {selectedLeaf && (
          <>
            <span className="text-slate-500">/</span>
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-slate-300"
            >
              {leaves.find((l) => l.id === selectedLeaf)?.title}
            </motion.span>
          </>
        )}
      </div>
    </div>
  );
}
